import { useIonRouter } from '@ionic/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import moment from 'moment';

import { usePets } from '../../context/PetsContext';
import { DatosPago, MedioPagoReq, MetodoPago } from '../../types/Pago/types';
import {
  Pet,
  PetForm_1 as PetFormType_1,
  PetForm_3 as PetFormType_3,
  PetReq,
  SolicitudCambioPetReq,
} from '../../types/Pet/types';
import {
  isDuplicatedMedioPago,
  isTipoCBU,
  normalizeNumeroPago,
  normalizeVencimiento,
} from '../../utils/pago';
import { hasChangesInPetBody } from '../../utils/pet';
import { capitalizeFirstLetter } from '../../utils/string';
import { useGetDatosPago, useGetMetodosPago, usePostDatosPago } from '../Pago/usePago';
import { useGetPlanes, useGetPreciosPlanesByEdadMascota } from '../Plan/usePlanes';
import { useAlert } from '../UI/useAlert';
import { useStepNavigation } from '../UI/useStepNavigation';
import { useRazas } from './usePetUtils';
import { usePostPet, useSolicitudCambioPet } from './usePet';

// Tipos
interface RazaOption {
  value: string;
  label: string;
}
interface RazasByEspecie {
  perro: RazaOption[];
  gato: RazaOption[];
}
interface MetodosPagoByTipo {
  tarjeta: MetodoPago[];
  cbu: MetodoPago[];
}
interface UsePetFormProps {
  petData?: Pet;
  closeModal?: () => void;
  totalSteps: number;
}

// Defaults
const RAZAS_DEFAULT: RazasByEspecie = { perro: [], gato: [] };
const METODOS_PAGO_DEFAULT: MetodosPagoByTipo = { tarjeta: [], cbu: [] };

export const usePetForm = ({ petData, closeModal, totalSteps }: UsePetFormProps) => {
  const { push } = useIonRouter();
  const isEditMode = !!petData;

  // API Hooks
  const { mutate: mutateMascotas } = usePets();
  const { postDatosPago } = usePostDatosPago();
  const { postPet, isLoading: isPostPetLoading } = usePostPet();
  const { postSolicitudCambioPet, isLoading: isSolicitudLoading } =
    useSolicitudCambioPet();
  const { razas, isValidating: isRazasValidating } = useRazas();
  const { planes, isValidating: isPlanesValidating } = useGetPlanes(!isEditMode);

  const { datosPago, mutate: mutateDatosPago } = useGetDatosPago(!isEditMode);
  const { metodosPago } = useGetMetodosPago(!isEditMode);

  // UI Hooks
  const { alertState, showAlert, hideAlert } = useAlert();
  const {
    currentStep,
    totalSteps: steps,
    isFirst,
    next,
    back,
  } = useStepNavigation(totalSteps);

  // Estado Local
  const [pet, setPet] = useState<PetFormType_1 | null>(petData ?? null);
  const [plan, setPlan] = useState<number>();
  const [datosPagoPreloaded, setDatosPagoPreloaded] = useState<DatosPago | null>(null);
  const [codVendedor, setCodVendedor] = useState<string | null>(null);
  const [isCodVendedorAlertOpen, setIsCodVendedorAlertOpen] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  const datosPagoAlertShownRef = useRef(false);
  const codVendedorAlertShownRef = useRef(false);
  const hadPreexistingDatosPagoRef = useRef<boolean | null>(null);
  const isOnSubmittingRef = useRef(false);

  const edadMascota = useMemo(() => {
    if (!pet?.fecha_nacimiento) return null;
    return moment().diff(moment(pet.fecha_nacimiento), 'years');
  }, [pet]);

  const { preciosPlanes, isValidating: isPreciosPlanesValidating } =
    useGetPreciosPlanesByEdadMascota(edadMascota, !isEditMode);

  // Helper para alertas de error
  const showError = useCallback(
    (message: string, onDismiss?: () => void) => {
      showAlert({
        header: 'Error',
        message,
        buttons: [{ text: 'Entendido' }],
        onDidDismiss: onDismiss,
      });
    },
    [showAlert],
  );

  const handleCloseAndRedirect = useCallback(() => {
    closeModal?.();
    setTimeout(() => push('/tabs/home', 'none', 'replace'), 50);
  }, [closeModal, push]);

  // Submit: Alta de mascota
  const onSubmit = useCallback(
    async (medioPagoValues: PetFormType_3): Promise<boolean> => {
      if (isOnSubmittingRef.current) return false;

      if (!pet || !plan) {
        showError('Por favor completá todos los campos requeridos.');
        return false;
      }

      isOnSubmittingRef.current = true;
      try {
        const tipo = Number(medioPagoValues.tipo);
        const esCBU = !!medioPagoValues.cbu;
        const noRequiereVencimiento = esCBU || tipo === 30 || tipo === 31;

        const medioPagoFormatted: MedioPagoReq = {
          tipo,
          numero_pago: normalizeNumeroPago(
            medioPagoValues.numeroTarjeta ?? medioPagoValues.cbu,
          ),
          vencimiento: noRequiereVencimiento ? null : normalizeVencimiento(medioPagoValues.vencimiento),
        };

        const pagoYaExiste = datosPago?.some((p) =>
          isDuplicatedMedioPago(p, medioPagoFormatted),
        );

        if (!pagoYaExiste && !(await postDatosPago(medioPagoFormatted))) {
          showError('No pudimos guardar los datos de pago.');
          return false;
        }

        const datosActualizados = await mutateDatosPago();
        if (!datosActualizados?.length) {
          showError('No se pudieron recuperar los datos de pago.');
          return false;
        }

        const ultimoPago = pagoYaExiste
          ? datosActualizados.find((p) => isDuplicatedMedioPago(p, medioPagoFormatted))
          : datosActualizados.at(-1);

        if (!ultimoPago) {
          showError('No se pudo determinar el método de pago.');
          return false;
        }

        const body: PetReq = {
          datos_mascota: { ...pet, animal: capitalizeFirstLetter(pet.animal) },
          datos_pago: {
            tipo: medioPagoFormatted.tipo,
            numero_pago: normalizeNumeroPago(ultimoPago.numero_pago),
            vencimiento: noRequiereVencimiento ? null : medioPagoFormatted.vencimiento,
            plan: plan,
          },
          codigo_vendedor: codVendedor ?? '',
        };

        if (!(await postPet(body))) {
          showError('No pudimos registrar tu mascota.');
          return false;
        }

        showAlert({
          header: '¡Éxito!',
          message: 'Registramos a tu mascota correctamente.',
          buttons: [{ text: 'Entendido' }],
          onDidDismiss: async () => {
            await mutateMascotas();
            handleCloseAndRedirect();
          },
        });
        return true;
      } catch (err) {
        console.error(err);
        showError('Ocurrió un error inesperado.');
        return false;
      } finally {
        isOnSubmittingRef.current = false;
      }
    },
    [
      pet,
      plan,
      codVendedor,
      datosPago,
      postDatosPago,
      mutateDatosPago,
      postPet,
      mutateMascotas,
      showAlert,
      showError,
      handleCloseAndRedirect,
    ],
  );

  // Submit: Solicitud de cambio
  const submitSolicitud = useCallback(
    async (petFormData?: PetFormType_1): Promise<boolean> => {
      const currentPet = petFormData ?? pet;

      if (!currentPet || !petData) {
        showError('No se encontraron los datos de la mascota.');
        return false;
      }

      try {
        const body: SolicitudCambioPetReq = {
          mascotaId: petData.id,
          mascotaNombre: currentPet.nombre,
          mascotaEspecie: currentPet.animal,
          mascotaSexo: currentPet.sexo,
          mascotaRaza: currentPet.raza,
          mascotaFechaNac: currentPet.fecha_nacimiento,
          mascotaColor: currentPet.color,
        };

        if (!hasChangesInPetBody(petData, body)) {
          showAlert({
            header: 'Sin cambios',
            message:
              'No se detectaron modificaciones en los datos de tu mascota. Realizá al menos un cambio para enviar la solicitud.',
            buttons: [{ text: 'Entendido' }],
          });
          return false;
        }

        if (!(await postSolicitudCambioPet(body))) {
          showError('No pudimos registrar tu solicitud de cambio.');
          return false;
        }

        showAlert({
          header: '¡Éxito!',
          message:
            'Se envio una solicitud de cambio de datos de su mascota. Los cambios serán revisados por nuestro equipo y se aplicarán en los próximos días.',
          buttons: [{ text: 'Entendido' }],
          onDidDismiss: closeModal,
        });
        return true;
      } catch (error) {
        console.error('Error al enviar solicitud:', error);
        showError('Ocurrió un problema. Intentá nuevamente más tarde.');
        return false;
      }
    },
    [pet, petData, postSolicitudCambioPet, showAlert, showError, closeModal],
  );

  // Effects

  // Capturar si el usuario ya tenía datos de pago ANTES de iniciar este flujo
  // Esto se ejecuta una sola vez cuando datosPago está disponible
  useEffect(() => {
    if (hadPreexistingDatosPagoRef.current === null && datosPago !== undefined) {
      hadPreexistingDatosPagoRef.current = datosPago.length > 0;
    }
  }, [datosPago]);

  // Mostrar alerta de autocompletado SOLO si el usuario ya tenía datos de pago previos
  // Esto evita mostrar la alerta cuando el usuario acaba de ingresar sus datos por primera vez
  useEffect(() => {
    if (
      currentStep === 3 &&
      !datosPagoAlertShownRef.current &&
      datosPago?.length &&
      hadPreexistingDatosPagoRef.current === true
    ) {
      datosPagoAlertShownRef.current = true;
      const lastPago = datosPago.at(-1)!;
      const esCBU = isTipoCBU(lastPago.tipo, lastPago.medioPago?.verifica);

      const tipoPago = capitalizeFirstLetter(lastPago.tipo);
      const numeroCorto = `****${lastPago.numero_pago.slice(-4)}`;
      const vencimientoStr = lastPago.vencimiento
        ? ` (vence ${moment(lastPago.vencimiento).format('MM/YY')})`
        : '';

      showAlert({
        header: 'Usar datos guardados',
        message: esCBU
          ? `Encontramos tu ${tipoPago} terminado en ${numeroCorto}. ¿Querés usarlo para este pago?`
          : `Encontramos tu tarjeta ${tipoPago} terminada en ${numeroCorto}${vencimientoStr}. ¿Querés usarla para este pago?`,
        buttons: [
          { text: 'No, cargar otro', role: 'cancel' },
          {
            text: 'Sí, usar este',
            handler: () => setDatosPagoPreloaded(lastPago),
          },
        ],
      });
    }
  }, [currentStep, datosPago, showAlert]);

  useEffect(() => {
    if (!isRazasValidating) setIsInitialized(true);
  }, [isRazasValidating]);

  return {
    /* =======================
     * Core / Dominio
     * ======================= */
    pet,
    setPet,
    plan,
    setPlan,
    datosPagoPreloaded,

    /* =======================
     * Datos externos (API / catálogos)
     * ======================= */
    razas: razas ?? RAZAS_DEFAULT,
    planes: planes ?? [],
    preciosPlanes: preciosPlanes ?? [],
    datosPago: datosPago ?? [],
    metodosPago: metodosPago ?? METODOS_PAGO_DEFAULT,

    /* =======================
     * Submit / side effects
     * ======================= */
    onSubmit,
    submitSolicitud,
    isSubmitting: isPostPetLoading || isSolicitudLoading,

    /* =======================
     * Estado de flujo / validaciones
     * ======================= */
    isInitialized,
    isValidatingRazas: isRazasValidating,
    isValidatingPlanes: isPlanesValidating || isPreciosPlanesValidating,
    isCodVendedorAlertOpen,
    setIsCodVendedorAlertOpen,

    /* =======================
     * Navegación (wizard)
     * ======================= */
    currentStep,
    totalSteps: steps,
    isFirst,
    next,
    back,

    /* =======================
     * UI / Alertas
     * ======================= */
    alertState,
    showAlert,
    hideAlert,

    /* =======================
     * Código vendedor (lógica puntual)
     * ======================= */
    codVendedor,
    handleCodVendedorSubmit: useCallback((code: string) => setCodVendedor(code), []),
    codVendedorAlertShownRef,
  };
};
